import React from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    Legend,
    Customized,
} from "recharts";

interface VideoViewsByCountry {
    VDate: string;
    CountryName: string;
    TotalViews: number;
}

interface TotalViewsByCountryProps {
    countryBreakdownData: VideoViewsByCountry[];
}

const TotalViewsByCountry: React.FC<TotalViewsByCountryProps> = ({ countryBreakdownData }) => {
    if (!countryBreakdownData || countryBreakdownData.length === 0) {
        return <p style={{ color: "red", textAlign: "center" }}>No data available for the selected date range.</p>;
    }

    // Flatten data with VDate
    const flattenedData = countryBreakdownData.map(({ VDate, CountryName, TotalViews }) => ({
        VDate: String(VDate),
        Country: CountryName.trim(),
        TotalViews,
    }));

    // Group rows by VDate for the div-based details section
    const rowsByDate = flattenedData.reduce<Record<string, { Country: string; TotalViews: number }[]>>((acc, row) => {
        if (!acc[row.VDate]) acc[row.VDate] = [];
        acc[row.VDate].push({ Country: row.Country, TotalViews: row.TotalViews });
        return acc;
    }, {});

    // Sort dates ascending for display (optional)
    const sortedDates = Object.keys(rowsByDate).sort();

    // Determine Y-axis max value
    const maxViews = Math.max(...flattenedData.map((d) => d.TotalViews));

    return (
        <div style={{ width: "100%", padding: "20px", backgroundColor: "#f8f9fa", borderRadius: "10px" }}>
            <h2 style={{ textAlign: "center", color: "#333" }}>Total Views by Country</h2>

            <ResponsiveContainer width="100%" height={450}>
                <BarChart data={flattenedData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }} barCategoryGap="20%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />

                    <Customized
                        component={(props) => {
                            const { xAxisMap, yAxisMap, data } = props as any;
                            if (!xAxisMap || !xAxisMap[0] || !data) return null;

                            const xScale = xAxisMap[0].scale;
                            const bandWidth = xScale.bandwidth?.() || 0;
                            const chartHeight = yAxisMap?.[0]?.height || 0;
                            const chartY = yAxisMap?.[0]?.y || 0;

                            const uniqueDates: string[] = Array.from(new Set(data.map((d: any) => String(d.VDate))));
                            const separators: JSX.Element[] = [];

                            for (let i = 1; i < uniqueDates.length; i++) {
                                const prevDate = uniqueDates[i - 1];
                                const currDate = uniqueDates[i];

                                const prevCountries = data
                                    .filter((d: any) => String(d.VDate) === prevDate)
                                    .map((d: any) => d.Country);
                                const currCountries = data
                                    .filter((d: any) => String(d.VDate) === currDate)
                                    .map((d: any) => d.Country);

                                const prevXPositions = prevCountries
                                    .map((c: string) => xScale(c) as number | undefined)
                                    .filter((x: number | undefined): x is number => x !== undefined);

                                const currXPositions = currCountries
                                    .map((c: string) => xScale(c) as number | undefined)
                                    .filter((x: number | undefined): x is number => x !== undefined);

                                if (prevXPositions.length === 0 || currXPositions.length === 0) continue;

                                const prevMaxX = Math.max(...prevXPositions) + bandWidth;
                                const currMinX = Math.min(...currXPositions);

                                if (!isNaN(prevMaxX) && !isNaN(currMinX) && currMinX > prevMaxX) {
                                    const separatorX = (prevMaxX + currMinX) / 2;

                                    separators.push(
                                        <line
                                            key={`separator-${currDate}`}
                                            x1={separatorX}
                                            y1={chartY}
                                            x2={separatorX}
                                            y2={chartY + chartHeight}
                                            stroke="red"
                                            strokeWidth={2}
                                            strokeDasharray="5 5"
                                        />
                                    );
                                }
                            }

                            return <>{separators}</>;
                        }}
                    />

                    <XAxis
                        dataKey="Country"
                        type="category"
                        tick={{ fontSize: 12, fontWeight: "bold" }}
                        angle={-75}
                        textAnchor="end"
                        interval={0}
                        height={120}
                    />

                    <YAxis scale="log" domain={[1, maxViews + 10]} />

                    <Tooltip formatter={(value, name, props) => [`${value} Views`, `${props.payload?.Country}`]} />

                    <Legend verticalAlign="top" wrapperStyle={{ fontSize: "14px" }} />

                    <Bar dataKey="TotalViews" name="Total Views" fill="#8884d8" />
                </BarChart>
            </ResponsiveContainer>

            {/* === DIV-BASED "TABLE" (no <table>, immune to global table CSS) === */}
            <div style={{ marginTop: "20px" }}>
                {sortedDates.map((dateKey) => {
                    const rows = rowsByDate[dateKey] || [];
                    // optional: sort rows by TotalViews desc inside each date group
                    // rows.sort((a, b) => b.TotalViews - a.TotalViews);

                    return (
                        <div key={dateKey} style={{ marginBottom: "18px" }}>
                            <h3 style={{ margin: "0 0 8px 0", color: "#333", textAlign: "left" }}>
                                {new Date(dateKey).toLocaleDateString("en-GB")}
                            </h3>

                            <div
                                role="table"
                                aria-label={`Country views for ${dateKey}`}
                                style={{
                                    width: "100%",
                                    border: "1px solid #ddd",
                                    borderRadius: "6px",
                                    overflow: "hidden",
                                    background: "#fff",
                                    minWidth: "480px",
                                }}
                            >
                                {/* Header */}
                                <div
                                    role="row"
                                    aria-rowindex={1}
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 140px",
                                        background: "#8884d8",
                                        color: "#fff",
                                        fontWeight: 600,
                                    }}
                                >
                                    <div role="columnheader" style={{ padding: "12px", borderBottom: "2px solid #ddd" }}>
                                        Country
                                    </div>
                                    <div role="columnheader" style={{ padding: "12px", borderBottom: "2px solid #ddd" }}>
                                        Views
                                    </div>
                                </div>

                                {/* Body */}
                                <div role="rowgroup">
                                    {rows.map((r, idx) => {
                                        const bg = idx % 2 === 0 ? "#ffffff" : "#f2f2f2"; // white / light grey
                                        return (
                                            <div
                                                key={`${dateKey}-${r.Country}-${idx}`}
                                                role="row"
                                                aria-rowindex={idx + 2}
                                                style={{
                                                    display: "grid",
                                                    gridTemplateColumns: "1fr 140px",
                                                    background: bg,
                                                }}
                                            >
                                                <div
                                                    role="cell"
                                                    style={{
                                                        padding: "12px",
                                                        borderBottom: "1px solid #ddd",
                                                        color: "#333",
                                                    }}
                                                >
                                                    {r.Country}
                                                </div>
                                                <div
                                                    role="cell"
                                                    style={{
                                                        padding: "12px",
                                                        borderBottom: "1px solid #ddd",
                                                        color: "#333",
                                                    }}
                                                >
                                                    {r.TotalViews}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ✅ Fix: Ensure the file is recognized as a module
export default TotalViewsByCountry;
